import { useRecoilCallback } from 'recoil'
import { caseAtom, objectSelector } from 'atoms'
import { MAX_FILLED_CASE_IMAGES } from '@messenger/consts'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { sameStringList } from '@/utils/editor/sameStringList'

/**
 * Rebuilds `caseAtom.filledImages`, the list of display URLs that template
 * photo slots cycle through, from the case's image records.
 *
 * Pass `freshImages` when the caller has just reloaded the list (for example
 * the value `loadCaseImages` resolves to). A Recoil callback's snapshot is
 * the last committed state, so a list set moments earlier is not in it until
 * React re-renders; without the argument the refill would write the old URLs
 * straight back. With no argument it reads the committed list, as before.
 */
export const useFillCaseImages = () => {
  const fillImages = useRecoilCallback(
    ({ snapshot, set }) =>
      (freshImages) => {
        countEditorProbeRender('fillCaseImages')
        const loadable = snapshot.getLoadable(caseImagesAtom)
        const images = Array.isArray(freshImages)
          ? freshImages
          : loadable.state === 'hasValue'
            ? loadable.contents
            : []

        const displayUrls = images.map((img) => img.displayUrl)

        let filled = []
        // HOTFIX (#2569): cap must match filledImagesSelector. See
        // apps/mdl-frontend/src/atoms/caseImageAtoms.ts for the rationale.
        while (
          filled.length < MAX_FILLED_CASE_IMAGES &&
          displayUrls.length > 0
        ) {
          filled = filled.concat(displayUrls)
        }
        filled = filled.slice(0, MAX_FILLED_CASE_IMAGES)

        const current = snapshot.getLoadable(caseAtom).valueMaybe()
        if (sameStringList(current?.filledImages, filled)) return

        set(
          objectSelector({ atom: caseAtom, property: 'filledImages' }),
          filled,
        )
      },
    [],
  )

  return fillImages
}

export default useFillCaseImages
